/**
 * Validation patterns for form inputs and data validation
 *
 * These patterns are shared between frontend and backend to ensure
 * consistent validation across the application.
 */

// ============================================================================
// VALIDATION PATTERNS - For checking if complete input is valid
// ============================================================================

/**
 * Email address format validation
 * Format: standard email (user@domain.tld)
 * Used by: Frontend validation, backend validation
 */
export const EMAIL_FORMAT = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * US ZIP code format validation
 * Format: 5 digits (12345) or 9 digits with dash (12345-6789)
 * Used by: Frontend validation, backend validation
 */
export const ZIP_CODE_FORMAT = /^\d{5}(-\d{4})?$/;

/**
 * US Visa number format validation
 * Format: 8 characters - either all digits (12345678) or 1 letter + 7 digits (A1234567)
 * Used by: Frontend validation, backend Zod schema
 */
export const VISA_NUMBER_FORMAT = /^(?:\d{8}|[A-Z]\d{7})$/;

/**
 * US Work Permit (EAD) number format validation
 * Format: 13 characters - 3 letters followed by 10 digits (e.g., EAC1234567890)
 * Common service center codes: EAC, WAC, LIN, SRC, NBC, MSC, IOE
 * Used by: Frontend validation, backend Zod schema
 */
export const WORK_PERMIT_NUMBER_FORMAT = /^[A-Z]{3}\d{10}$/;

/**
 * International passport number format validation
 * Format: 6-15 alphanumeric characters (letters and digits only, case-insensitive)
 *
 * Supports international passport formats:
 * - UK: 9 digits (e.g., 925665416)
 * - US: 9 digits (e.g., 123456789)
 * - Germany: 9-11 chars, letter + digits (e.g., C01X00T47)
 * - France: 8 chars, mixed (e.g., 14CV28142)
 * - China: 9 chars, letter + digits (e.g., E59659321)
 * - Canada: 8 chars, letters + digits (e.g., LA123456)
 * - Australia: 9 chars, letter + digits (e.g., M1234567)
 * - India: 8 chars, letter + digits (e.g., A1234567)
 *
 * Note: Accepts both uppercase and lowercase letters to accommodate various input methods.
 * The validation will normalize to uppercase during processing.
 *
 * Used by: Frontend validation, backend Zod schema
 */
export const PASSPORT_NUMBER_FORMAT = /^[A-Za-z0-9]{6,15}$/;

/**
 * Date string format validation
 * Format: MM/DD/YYYY
 * Used by: Frontend date validation
 */
export const DATE_STRING_FORMAT = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * UUID format validation (any version; hyphenated 8-4-4-4-12 hex).
 * Case-insensitive. Does not enforce RFC 4122 version/variant bits.
 * Used by: workflow-engine contract sweep handlers, shared ID checks
 */
export const UUID_FORMAT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * US Social Security Number format validation
 * Format: XXX-XX-XXXX with specific rules:
 * - Area number (first 3 digits): Cannot be 000, 666, or 900-999
 * - Group number (middle 2 digits): Cannot be 00
 * - Serial number (last 4 digits): Cannot be 0000
 * Used by: Frontend validation, backend validation
 *
 * Note: This regex validates the formatted SSN (with dashes). For unformatted input,
 * format the digits first: `${digits.slice(0,3)}-${digits.slice(3,5)}-${digits.slice(5,9)}`
 */
export const SSN_FORMAT = /^(?!000|666|9\d\d)\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/;

/**
 * Relaxed SSN format for lower environments (dev / test) only.
 *
 * Login.gov / IDM test accounts use SSNs in the 666 and 900-999 area ranges,
 * which the SSA rules above reject. This pattern allows those ranges while still
 * rejecting area 000, group 00, and serial 0000.
 * Impl and production must always validate with SSN_FORMAT.
 */
export const SSN_FORMAT_NON_PROD = /^(?!000)\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/;

/**
 * Invitation token embedded in `/api/v1/auth/invite/<token>` URLs.
 * Used for log redaction only; do not use this to validate route params.
 */
export const INVITATION_TOKEN_IN_AUTH_INVITE_URL_PATTERN = /(\/api\/v1\/auth\/invite\/)[a-fA-F0-9]{64}(?=\?|#|$)/g;

/**
 * Full invitation token string (64 hex characters).
 * Used for route param / raw token validation across auth and orchestrator.
 */
export const INVITATION_TOKEN_STRING_PATTERN = /^[a-fA-F0-9]{64}$/i;

// ============================================================================
// INPUT CLEANING PATTERNS - For removing invalid characters during input
// ============================================================================

/**
 * Removes all non-digit characters
 * Used for: Phone numbers, SSN, ZIP codes, alien registration numbers
 */
export const NON_DIGIT_CHARS = /\D/g;

/**
 * Removes all non-alphanumeric characters (keeps uppercase A-Z and 0-9)
 * Used for: Visa numbers, passport numbers (after uppercase conversion)
 */
export const NON_ALPHANUMERIC_CHARS = /[^A-Z0-9]/g;

/**
 * Removes all non-alphanumeric characters (case-insensitive, keeps a-z, A-Z, 0-9)
 * Used for: Work permit numbers, initial input before uppercase conversion
 */
export const NON_ALPHANUMERIC_CHARS_CASE_INSENSITIVE = /[^a-zA-Z0-9]/g;
